import React, { useCallback, useMemo, useState } from "react";
import { Link, withRouter } from "react-router-dom";
import {
  Button,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import HeaderPageItem from "../custom/HeaderPageItem";
import PageContainer, { CardPageItem } from "../custom/Page";
import { FormattedMessage } from "react-intl";
import { capitalCaseNormalizer, debounce } from "../util";
import UserTagStatus from "./components/UserTagStatus";
import { searchUsersAdmin } from "../actions";
import AbtcAvatar from "../custom/Avatar";
import { exportUsers } from "../util/exportCsv";
import InfiniteScrollAsync from "./components/InfiniteScrollAsync";
import MultiSelectDropdown from "./components/select/base/MultiSelectDropdown";

const ListRoles = [
  { name: "ADMIN", label: "app.enuns.ADMIN", value: true },
  { name: "CUSTOMER", label: "app.enuns.CUSTOMER", value: true },
  { name: "KYC", label: "app.enuns.KYC", value: true },
  { name: "FINANCE_MANAGER", label: "app.enuns.FINANCE_MANAGER", value: true },
];

const ListLevelNames = [
  { name: "AGENT", label: "app.enuns.levelName.AGENT", value: true },
  {
    name: "AGENT_MANAGER",
    label: "app.enuns.levelName.AGENT_MANAGER",
    value: true,
  },
  {
    name: "REGIONAL_MANAGER",
    label: "app.enuns.levelName.REGIONAL_MANAGER",
    value: true,
  },
  {
    name: "SALES_DIRECTOR",
    label: "app.enuns.levelName.SALES_DIRECTOR",
    value: true,
  },
  { name: "CCO", label: "app.enuns.levelName.CCO", value: true },
];

const PREMIUM = "PREMIUM";
const NOT_PREMIUM = "NOT-PREMIUM";
const PremiumValues = [
  { label: "app.premium", value: PREMIUM },
  { label: "app.not.premium", value: NOT_PREMIUM },
];

const BLOCKED = "BLOCKED";
const NOT_BLOCKED = "NOT-BLOCKED";
const BlockedValues = [
  { label: "app.user.isBlocked", value: BLOCKED },
  { label: "app.user.isNotBlocked", value: NOT_BLOCKED },
];

const DEFAULT_SEARCH = {
  name: "",
  userId: "",
  role: "",
  className: "",
};

const AdminUsers = () => {
  const [menuAnchor, setMenuAnchor] = useState();
  const [openDialogFilter, setOpenDialogFilter] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);

  const [listAllRoles, setListAllRoles] = useState(true);
  const [listRoles, setListRoles] = useState(
    ListRoles.map((item) => ({ ...item })),
  );
  const [listAllLevelNames, setListAllLevelNames] = useState(true);
  const [listLevelNames, setListLevelNames] = useState(
    ListLevelNames.map((item) => ({ ...item })),
  );
  const [premium, setPremium] = useState(null);
  const [blocked, setBlocked] = useState(null);

  const [searchName, setSearchName] = useState("");
  const [searchId, setSearchId] = useState("");

  const [search, setSearch] = useState(DEFAULT_SEARCH);
  const memoizedSearch = useMemo(() => search, [search]);

  const fetchUsersPaginate = useCallback(
    async ({ params, skip = 0, take = 10 }) => {
      const paramsWithPaging = {
        ...params,
        skip,
        take,
      };
      return searchUsersAdmin(paramsWithPaging);
    },
    [],
  );

  const handleSearchId = useCallback(
    debounce((value) => {
      setSearch({
        ...DEFAULT_SEARCH,
        userId: value,
      });
    }),
    [],
  );

  const handleSearchName = useCallback(
    debounce((value) => {
      setSearch({
        ...DEFAULT_SEARCH,
        name: value,
      });
    }),
    [],
  );

  const handleConfirmFilters = () => {
    const {
      premium: _premium,
      isBlocked: _isBlocked,
      role: _role,
      className: _className,
      ...newSearch
    } = search;

    if (premium !== null) {
      newSearch.premium = premium;
    }
    if (blocked !== null) {
      newSearch.isBlocked = blocked;
    }
    if (!listRoles.every((lr) => lr.value)) {
      newSearch.role = listRoles
        .filter((item) => item.value)
        .map((item) => item.name)
        .join(",");
    }
    if (!listLevelNames.every((lr) => lr.value)) {
      newSearch.className = listLevelNames
        .filter((item) => item.value)
        .map((item) => item.name)
        .join(",");
    }
    setSearch(newSearch);
    setOpenDialogFilter(false);
  };

  const downloadUsersCSV = async () => {
    setLoadingExport(true);
    const SAFE_TAKE = 2147483647;
    const allUsers = await searchUsersAdmin({
      ...search,
      skip: 0,
      take: SAFE_TAKE,
    });
    exportUsers(allUsers);
    setMenuAnchor(undefined);
    setLoadingExport(false);
  };

  const onChangeAllRoles = (event) => {
    setListRoles(
      listRoles.map((role) => {
        role.value = event.target.checked;
        return role;
      }),
    );
    setListAllRoles(event.target.checked);
  };

  const onChangeRole = (event) => {
    let all = true;
    const newListRoles = listRoles.map((role) => {
      if (role.name === event.target.name) {
        role.value = event.target.checked;
      }
      if (!role.value) {
        all = false;
      }
      return role;
    });
    setListRoles(newListRoles);
    setListAllRoles(all);
  };

  const onChangeAllLevelNames = (event) => {
    setListAllLevelNames(
      listLevelNames.map((levelName) => {
        levelName.value = event.target.checked;
        return levelName;
      }),
    );
    setListAllLevelNames(event.target.checked);
  };

  const onChangeLevelName = (event) => {
    let all = true;
    const newListLevelNames = listLevelNames.map((levelName) => {
      if (levelName.name === event.target.name) {
        levelName.value = event.target.checked;
      }
      if (!levelName.value) {
        all = false;
      }
      return levelName;
    });
    setListLevelNames(newListLevelNames);
    setListAllLevelNames(all);
  };

  const onChangePremium = (values) => {
    if (!values.length || values.length === PremiumValues.length) {
      setPremium(null);
    }
    setPremium(values.includes(PREMIUM));
  };

  const onChangeIsBlocked = (values) => {
    if (!values.length || values.length === BlockedValues.length) {
      setBlocked(null);
    }
    setBlocked(values.includes(BLOCKED));
  };

  return (
    <>
      <PageContainer>
        <HeaderPageItem
          title="app.users"
          onMore={(e) => setMenuAnchor(e.target)}
        >
          <Menu
            onClose={(e) => setMenuAnchor(undefined)}
            open={!!menuAnchor}
            anchorEl={menuAnchor}
            disableScrollLock
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(undefined);
                setPremium(null);
                setBlocked(null);
                setOpenDialogFilter(true);
              }}
            >
              <FormattedMessage id="app.filter" />
            </MenuItem>
            <MenuItem
              onClick={downloadUsersCSV}
              disabled={loadingExport}
              onClose={(e) => setMenuAnchor(undefined)}
            >
              <FormattedMessage id="app.users.export" />
            </MenuItem>
          </Menu>
        </HeaderPageItem>

        <CardPageItem raised>
          <CardContent>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "3fr 1fr",
                gridGap: 8,
                marginTop: 16,
              }}
            >
              <TextField
                id="searchName"
                value={searchName}
                label={<FormattedMessage id="app.search.nameemail" />}
                variant="outlined"
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={(e) => {
                  handleSearchName(e.target.value);
                  setSearchName(e.target.value);
                  setSearchId("");
                }}
              />
              <TextField
                id="searchId"
                value={searchId}
                label={<FormattedMessage id="app.search.id" />}
                variant="outlined"
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={(e) => {
                  handleSearchId(e.target.value);
                  setSearchId(e.target.value);
                  setSearchName("");
                }}
              />
            </div>
          </CardContent>
        </CardPageItem>

        <CardPageItem>
          <CardContent>
            <List>
              <InfiniteScrollAsync
                emptyMessage={"app.users.not.filter"}
                offsetIncrement={50}
                onScroll={fetchUsersPaginate}
                params={memoizedSearch}
                take={20}
                render={(user) => (
                  <ListItem
                    button
                    alignItems="flex-start"
                    key={user.id}
                    component={Link}
                    to={"/user/" + user.id}
                  >
                    <ListItemAvatar>
                      <AbtcAvatar imageUrl={user.photoUrl} disableLink />
                    </ListItemAvatar>
                    <ListItemText
                      primaryTypographyProps={{ component: "div" }}
                      secondaryTypographyProps={{ component: "div" }}
                      primary={
                        <div
                          color="textSecondary"
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            color={user.premium ? "primary" : undefined}
                          >
                            {capitalCaseNormalizer(user.name)}
                          </Typography>
                          <span> {"Id: " + user.id}</span>
                        </div>
                      }
                      secondary={
                        <div>
                          <div
                            color="textSecondary"
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{user.email}</span>
                            {user.premium && (
                              <Typography color="primary" variant="caption">
                                <FormattedMessage id={"app.premium"} />
                              </Typography>
                            )}
                          </div>
                          <span>
                            (<FormattedMessage id={"app.enuns." + user.role} />)
                          </span>
                          <UserTagStatus
                            variant="body2"
                            apt={user.apt}
                            isBlocked={user.isBlocked}
                          />
                        </div>
                      }
                    />
                  </ListItem>
                )}
              />
            </List>
          </CardContent>
        </CardPageItem>
      </PageContainer>

      <Dialog open={openDialogFilter} fullWidth disableScrollLock>
        <DialogContent
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1em",
          }}
        >
          <FormControl component="fieldset">
            <FormLabel component="legend">
              <FormattedMessage id="app.profile.filter" />
            </FormLabel>
            <FormGroup></FormGroup>
            <FormControlLabel
              key={"all"}
              control={
                <Checkbox
                  checked={listAllRoles}
                  onChange={onChangeAllRoles}
                  color="primary"
                />
              }
              label={<FormattedMessage id="app.all" />}
            />
            <Divider />
            <FormGroup>
              {listRoles.map((role) => (
                <FormControlLabel
                  key={role.name}
                  control={
                    <Checkbox
                      checked={role.value}
                      onChange={onChangeRole}
                      name={role.name}
                      color="primary"
                    />
                  }
                  label={<FormattedMessage id={role.label} />}
                />
              ))}
            </FormGroup>
          </FormControl>

          <FormControl component="fieldset">
            <FormLabel component="legend">
              <FormattedMessage id="app.profile.filter.management" />
            </FormLabel>
            <FormGroup></FormGroup>
            <FormControlLabel
              key={"allLevelName"}
              control={
                <Checkbox
                  checked={listAllLevelNames}
                  onChange={onChangeAllLevelNames}
                  color="primary"
                />
              }
              label={<FormattedMessage id="app.all" />}
            />
            <Divider />
            <FormGroup>
              {listLevelNames.map(({ name, label, value }) => (
                <FormControlLabel
                  key={name}
                  control={
                    <Checkbox
                      checked={value}
                      onChange={onChangeLevelName}
                      name={name}
                      color="primary"
                    />
                  }
                  label={<FormattedMessage id={label} />}
                />
              ))}
            </FormGroup>
          </FormControl>

          <MultiSelectDropdown
            title="app.premium"
            values={PremiumValues}
            onChange={onChangePremium}
            defaultSelected={PremiumValues.map(({ value }) => value)}
          />

          <MultiSelectDropdown
            title="app.user.isBlocked"
            values={BlockedValues}
            onChange={onChangeIsBlocked}
            defaultSelected={BlockedValues.map(({ value }) => value)}
          />
        </DialogContent>
        <DialogActions>
          <Grid
            container
            direction="row"
            justify="flex-end"
            alignItems="center"
          >
            <Button
              onClick={() => {
                handleConfirmFilters();
                setOpenDialogFilter(false);
              }}
              color="primary"
            >
              <FormattedMessage id="app.confirm" />
            </Button>
            <Button onClick={() => setOpenDialogFilter(false)} color="primary">
              <FormattedMessage id="app.cancel" />
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default withRouter(AdminUsers);
